import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { helpAPI } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  ArrowLeft, Search, Rocket, FileText, Package, Truck,
  ShoppingCart, Briefcase, ChevronRight, HelpCircle, BookOpen, Home
} from 'lucide-react';

const ICONS = {
  'rocket': Rocket,
  'file-text': FileText,
  'package': Package,
  'truck': Truck,
  'shopping-cart': ShoppingCart,
  'briefcase': Briefcase,
};

export default function HelpPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(true);

  // Get category and article from URL
  const categoryParam = searchParams.get('category');
  const articleParam = searchParams.get('article');

  // Load categories
  useEffect(() => {
    const loadCategories = async () => {
      try {
        const res = await helpAPI.getCategories();
        setCategories(res.data || []);
      } catch (err) {
        console.error('Failed to load help categories', err);
      } finally {
        setLoading(false);
      }
    };
    loadCategories();
  }, []);

  // Load article when URL changes
  useEffect(() => {
    const loadContent = async () => {
      if (categoryParam && articleParam) {
        try {
          const res = await helpAPI.getArticle(categoryParam, articleParam);
          setSelectedArticle(res.data);
          setSelectedCategory(categoryParam);
        } catch (err) {
          console.error('Failed to load article', err);
        }
      } else if (categoryParam) {
        try {
          const res = await helpAPI.getCategoryArticles(categoryParam);
          setSelectedCategory(res.data);
          setSelectedArticle(null);
        } catch (err) {
          console.error('Failed to load category', err);
        }
      } else {
        setSelectedCategory(null);
        setSelectedArticle(null);
      }
    };
    loadContent();
  }, [categoryParam, articleParam]);

  // Search
  const handleSearch = useCallback(async (q) => {
    if (q.length < 2) {
      setSearchResults([]);
      return;
    }
    try {
      const res = await helpAPI.search(q);
      setSearchResults(res.data || []);
    } catch (err) {
      console.error('Search failed', err);
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => handleSearch(searchQuery), 300);
    return () => clearTimeout(timer);
  }, [searchQuery, handleSearch]);

  const goToCategory = (catId) => {
    setSearchParams({ category: catId });
    setSearchQuery('');
    setSearchResults([]);
  };

  const goToArticle = (catId, artId) => {
    setSearchParams({ category: catId, article: artId });
    setSearchQuery('');
    setSearchResults([]);
  };

  const goHome = () => {
    setSearchParams({});
    setSearchQuery('');
    setSearchResults([]);
  };

  // Render article content
  const renderArticle = () => {
    if (!selectedArticle) return null;
    const article = selectedArticle;

    return (
      <div className="space-y-6" data-testid="help-article">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Button variant="ghost" size="sm" onClick={goHome} className="h-6 px-2">
            <Home className="w-3 h-3 mr-1" /> Help
          </Button>
          <ChevronRight className="w-3 h-3" />
          <Button variant="ghost" size="sm" onClick={() => goToCategory(article.category_id)} className="h-6 px-2">
            {article.category_title}
          </Button>
          <ChevronRight className="w-3 h-3" />
          <span className="font-medium text-foreground">{article.title}</span>
        </div>

        {/* Article Title */}
        <div>
          <h1 className="text-2xl font-bold">{article.title}</h1>
        </div>

        {/* Steps */}
        <div className="space-y-4">
          {article.steps?.map((step, idx) => (
            <Card key={idx} className="overflow-hidden">
              <CardContent className="p-4">
                <div className="flex gap-3">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-blue-600 text-white flex items-center justify-center font-bold text-sm">
                    {step.step}
                  </div>
                  <div className="flex-1 space-y-3">
                    <p className="text-sm" dangerouslySetInnerHTML={{ 
                      __html: step.text.replace(/\*\*(.*?)\*\*/g, '<strong class="text-blue-600">$1</strong>') 
                    }} />
                    
                    {/* Substeps */}
                    {step.substeps && (
                      <ul className="list-disc list-inside space-y-1 text-sm text-muted-foreground ml-2">
                        {step.substeps.map((sub, subIdx) => (
                          <li key={subIdx} dangerouslySetInnerHTML={{ 
                            __html: sub.replace(/\*\*(.*?)\*\*/g, '<strong class="text-foreground">$1</strong>') 
                          }} />
                        ))}
                      </ul>
                    )}

                    {/* Image */}
                    {step.image && (
                      <div className="mt-3 rounded-lg overflow-hidden border shadow-sm">
                        <img 
                          src={step.image} 
                          alt={`Step ${step.step}`} 
                          className="w-full h-auto"
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Notes */}
        {article.notes && article.notes.length > 0 && (
          <Card className="bg-amber-50/50 border-amber-200">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium flex items-center gap-1.5 text-amber-800">
                <HelpCircle className="w-4 h-4" /> Notes
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="list-disc list-inside space-y-1 text-sm text-amber-900">
                {article.notes.map((note, idx) => (
                  <li key={idx}>{note}</li>
                ))}
              </ul>
            </CardContent>
          </Card>
        )}
      </div>
    );
  };

  // Render category articles list
  const renderCategoryArticles = () => {
    if (!selectedCategory || typeof selectedCategory !== 'object') return null;
    const cat = selectedCategory;

    return (
      <div className="space-y-4" data-testid="help-category">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Button variant="ghost" size="sm" onClick={goHome} className="h-6 px-2">
            <Home className="w-3 h-3 mr-1" /> Help
          </Button>
          <ChevronRight className="w-3 h-3" />
          <span className="font-medium text-foreground">{cat.title}</span>
        </div>

        <h1 className="text-2xl font-bold">{cat.title}</h1>

        <div className="grid gap-3">
          {cat.articles?.map((article) => (
            <Card 
              key={article.id} 
              className="cursor-pointer hover:shadow-md transition-shadow"
              onClick={() => goToArticle(cat.id, article.id)}
            >
              <CardContent className="p-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <BookOpen className="w-5 h-5 text-blue-600" />
                  <span className="font-medium">{article.title}</span>
                </div>
                <ChevronRight className="w-4 h-4 text-muted-foreground" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  };

  // Render categories home
  const renderCategoriesHome = () => (
    <div className="space-y-6" data-testid="help-home">
      <div className="text-center space-y-2">
        <h1 className="text-3xl font-bold">BHPL-ERP Help Center</h1>
        <p className="text-muted-foreground">How can we help you today?</p>
      </div>

      {/* Search */}
      <div className="max-w-xl mx-auto relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input
          placeholder="Search help articles..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10"
          data-testid="help-search"
        />
        
        {/* Search Results Dropdown */}
        {searchResults.length > 0 && (
          <Card className="absolute w-full mt-1 z-50 shadow-lg">
            <CardContent className="p-2 max-h-64 overflow-y-auto">
              {searchResults.map((result, idx) => (
                <div
                  key={idx}
                  className="p-2 hover:bg-muted rounded cursor-pointer"
                  onClick={() => goToArticle(result.category_id, result.article_id)}
                >
                  <p className="font-medium text-sm">{result.article_title}</p>
                  <p className="text-xs text-muted-foreground">
                    {result.category_title} • Match in {result.match}
                  </p>
                </div>
              ))}
            </CardContent>
          </Card>
        )}
      </div>

      {/* Categories Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {categories.map((cat) => {
          const IconComponent = ICONS[cat.icon] || FileText;
          return (
            <Card 
              key={cat.id} 
              className="cursor-pointer hover:shadow-lg transition-all hover:border-blue-300"
              onClick={() => goToCategory(cat.id)}
            >
              <CardContent className="p-6 text-center space-y-3">
                <div className="w-12 h-12 mx-auto rounded-full bg-blue-100 flex items-center justify-center">
                  <IconComponent className="w-6 h-6 text-blue-600" />
                </div>
                <h3 className="font-semibold">{cat.title}</h3>
                <Badge variant="secondary" className="text-xs">
                  {cat.article_count} articles
                </Badge>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <HelpCircle className="w-8 h-8 animate-pulse text-muted-foreground mx-auto mb-2" />
          <p className="text-sm text-muted-foreground">Loading help...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-40">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <div className="flex items-center gap-2">
              <HelpCircle className="w-5 h-5 text-blue-600" />
              <span className="font-semibold">Help & Documentation</span>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={() => navigate('/')}>
            Back to App
          </Button>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-5xl mx-auto px-4 py-8">
        {selectedArticle ? renderArticle() : 
         (selectedCategory && typeof selectedCategory === 'object') ? renderCategoryArticles() : 
         renderCategoriesHome()}
      </div>
    </div>
  );
}
